import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { WIDGETS, camOn, feedAudible, feedVisible, pipAudible, showsRaceList, type StreamSource } from '@rabbitfuel/shared';
import { useRealtime } from '../lib/realtime.tsx';
import { useFullscreen, useMediaQuery, useStageSurface, useWakeLock } from '../lib/ui.ts';
import { peerStateLabel, useSubscriptions, useVideoStream } from '../lib/useMedia.ts';
import { useHiddenLayers, useLayerZoom, withoutHidden } from '../lib/viewerLayers.ts';
import { ChatPanel } from '../components/ChatPanel.tsx';
import { Chevron, ConnPill, StageLegal } from '../components/Chrome.tsx';
import { AppearanceControls } from '../components/AppearanceControls.tsx';
import { LayerMenu, LayersIcon, type LayerItem } from '../components/LayerMenu.tsx';
import { LiveTicker, tickerHasList, useTickerView } from '../components/Ticker.tsx';
import {
  StreamOverlay,
  courseChrome,
  courseFullscreen,
  isSplit,
  playerChrome,
  widgetHasContent,
} from '../components/StreamOverlay.tsx';
import { courseOf } from '../lib/course.ts';
import { PlayerBackdrop, backdropChrome } from '../components/PlayerBackdrop.tsx';
import { StandbyCard } from '../components/Standby.tsx';
import { SplitFill } from '../components/SplitFill.tsx';
import {
  AdFrameLayer,
  AdLabel,
  AdSpot,
  adChrome,
  adFrameChrome,
  sponsorBug,
  useAdBreak,
  useAdFrame,
} from '../components/Ads.tsx';
import { ClipRecorder } from '../components/ClipRecorder.tsx';
import { lookOf, videoLook } from '../lib/look.ts';
import { roleLabel } from '../lib/session.ts';

/*
 * Hochkant und quer neben dem Chat passen drei Zeilen der Strecken-Liste ins
 * Bild. Die Liste muss
 * dann auch in Dreiern blaettern: blaettert sie in Fuenfern und das Bild zeigt
 * nur die ersten drei, kommen Zeile vier und fuenf nie an die Reihe.
 */
const COMPACT_LIST_ROWS = 3;

type WatchPane = 'chat' | 'race';
const PANE_KEY = 'rabbitfuel:watch:pane';

/** Welcher Reiter unter dem Video offen ist -- gemerkt, wie man ihn zuletzt verlassen hat. */
function useWatchPane() {
  const [pane, setPane] = useState<WatchPane>(() => {
    try {
      return localStorage.getItem(PANE_KEY) === 'race' ? 'race' : 'chat';
    } catch {
      return 'chat';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(PANE_KEY, pane);
    } catch {
      /* Private Mode o.ae. -- dann eben ab Chat. */
    }
  }, [pane]);
  return [pane, setPane] as const;
}

const CHAT_OPEN_KEY = 'rabbitfuel:watch:chat';

/**
 * Chat rechts offen oder eingeklappt -- wie in der Regie, und wie dort merkt
 * es sich der Browser: wer nur schauen will, klappt ihn einmal weg und findet
 * ihn beim naechsten Event so wieder. Gilt nur neben dem Video; in einer
 * Spalte schalten ohnehin die Reiter.
 */
function useChatOpen() {
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(CHAT_OPEN_KEY) !== '0';
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(CHAT_OPEN_KEY, open ? '1' : '0');
    } catch {
      /* Private Mode o.ae. -- dann eben nur bis zum Neuladen. */
    }
  }, [open]);
  return [open, setOpen] as const;
}

export function Watch() {
  const { slug = '' } = useParams();
  useStageSurface();
  const { state, send } = useRealtime();
  const { sources, activeSourceId, overlaySourceId } = state;
  // Browser blocken Autoplay mit Ton -- deshalb stumm starten und per Klick oeffnen.
  const [muted, setMuted] = useState(true);
  /*
   * Solange jemand eine Botschaft aufnimmt, ist der Stream stumm -- sonst
   * landet sein Ton aus dem Lautsprecher mit in der Aufnahme. Danach wieder
   * wie vorher; die Elemente sind dann schon freigeschaltet.
   */
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const mutedBeforeClip = useRef<boolean | null>(null);
  /** Aufnahmefenster -- geoeffnet ueber den Knopf am Video oder im Kasten unter dem Ticker. */
  const [clipOpen, setClipOpen] = useState(false);
  const onClipRecorder = useCallback((active: boolean) => {
    if (active) {
      mutedBeforeClip.current ??= mutedRef.current;
      setMuted(true);
    } else if (mutedBeforeClip.current !== null) {
      setMuted(mutedBeforeClip.current);
      mutedBeforeClip.current = null;
    }
  }, []);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const fullscreen = useFullscreen(stageRef);
  const [chatOverlay, setChatOverlay] = useState(false);

  /*
   * Handy hochkant: Live-Punkt, Quellenschild und Knoepfe stehen unter dem
   * Bild statt darauf. Auf rund 370 x 208 Pixeln bliebe neben ihnen kein
   * Platz fuer die Einblendungen der Regie -- sie wuerden sich gegenseitig
   * verdecken. Im Vollbild ist die Flaeche da, dort liegt alles wieder im Bild.
   */
  const narrow = useMediaQuery('(max-width: 640px)');
  const compact = narrow && !fullscreen.active;
  // Quer neben dem Chat ist das Bild kaum 300 Pixel hoch -- da stiesse eine
  // fuenfzeilige Liste unten ans Quellenschild. Im Vollbild ist die Hoehe da.
  const landscapePhone = useMediaQuery('(max-height: 520px) and (orientation: landscape)');
  const flat = landscapePhone && !fullscreen.active;

  /*
   * Eine Spalte (Handy hochkant, Tablet): Das Video bleibt oben stehen, darunter
   * fuellt ein Reiter den Rest -- Chat oder Ticker mit Kameras. So scrollt
   * immer genau eine Flaeche. Vorher stand der Chat mit fester Hoehe unten in
   * der scrollenden Seite, und wer mit dem Finger auf ihm landete, kam weder
   * im Chat noch auf der Seite weiter.
   */
  const stacked = useMediaQuery('(max-width: 900px)') && !landscapePhone;
  const [pane, setPane] = useWatchPane();
  const [chatOpen, setChatOpen] = useChatOpen();
  const chatFolded = !stacked && !chatOpen;
  const chatFoldRef = useRef<HTMLButtonElement | null>(null);
  const chatRailRef = useRef<HTMLButtonElement | null>(null);
  const foldChat = (open: boolean) => {
    setChatOpen(open);
    // Der Fokus wandert mit -- sonst steht er auf einem Knopf, der gerade verschwunden ist.
    requestAnimationFrame(() => (open ? chatFoldRef : chatRailRef).current?.focus());
  };
  // Teilnehmer oder letzte Ereignisse -- am Desktop im Ticker umgeschaltet,
  // in einer Spalte ueber die Reiter unter dem Video. Gemerkt wird beides gleich.
  const [tickerView, setTickerView] = useTickerView();

  // Einblendungen, die dieser Zuschauer fuer sich ausgeblendet oder in der Groesse veraendert hat.
  const layers = useHiddenLayers();
  const layerZoom = useLayerZoom();
  const [layerMenu, setLayerMenu] = useState(false);
  const layerButtonRef = useRef<HTMLButtonElement | null>(null);
  const closeLayerMenu = useCallback(() => setLayerMenu(false), []);

  const approved = useMemo(() => sources.filter((s) => s.status === 'approved'), [sources]);
  const active = approved.find((s) => s.id === activeSourceId) ?? null;
  const overlay = approved.find((s) => s.id === overlaySourceId) ?? null;
  const sport = state.event?.sport ?? 'sonstiges';
  const course = courseOf(state.event?.geofence);
  const widgets = useMemo(() => withoutHidden(state.widgets, layers.hidden), [state.widgets, layers.hidden]);
  /*
   * Streckenkarte im Vollbild: das Hauptbild laeuft klein in der Ecke weiter,
   * die zweite Kamera als kleines Fenster statt im Splitscreen. Blendet der
   * Zuschauer die Strecke fuer sich aus, ist alles wieder wie sonst.
   */
  const courseShown = courseFullscreen(widgets, state.ticker, course);
  // Im Splitscreen ist die zweite Kamera die halbe Sendung -- dann laesst sie
  // sich nicht ausblenden, sonst bliebe eine Haelfte leer.
  const split = isSplit(state.pip, overlay) && !courseShown;
  // „Ganz zeigen" im Splitscreen: der Rand jeder Haelfte bekommt eine unscharfe Kopie.
  const fill = split && state.pip.fit === 'contain';
  // Wer das kleine Fenster ausblendet, holt dessen Bild gar nicht erst --
  // auf dem Handy ist das Datenvolumen.
  const pipSource = overlay && (split || !layers.hidden.has('pip')) ? overlay : null;

  // Nur Hauptbild und kleines Fenster holen -- jede weitere Verbindung kostet
  // den Sender Upload. Die Leiste unten zeigt die anderen Kameras nur an.
  const wanted = useMemo(
    () => [active, pipSource].filter((s): s is StreamSource => s != null),
    [active, pipSource],
  );
  const { streams, states } = useSubscriptions(wanted);

  const mainStream = active ? streams[active.id] : null;
  const overlayStream = pipSource ? streams[pipSource.id] : null;
  const mainRef = useVideoStream(mainStream);
  const pipRef = useVideoStream(overlayStream);

  /*
   * Werbepause: der Spot gross mit Ton, das Live-Bild klein und stumm in der
   * Ecke. Es ist dasselbe <video> wie sonst -- es rutscht nur zur Seite,
   * damit nach der Pause nichts neu verbinden muss.
   */
  const ad = useAdBreak(state.ads, state.adsAt);
  const adOn = Boolean(ad.active);
  const spotRef = useRef<HTMLVideoElement | null>(null);
  // Banner und L-Form: das Live-Bild rueckt zusammen und bleibt zu hoeren.
  const frame = useAdFrame(state.ads, state.adsAt, adOn);

  // Wie jeder Videoplayer: solange ein Bild laeuft, bleibt der Bildschirm an.
  useWakeLock(Boolean(mainStream) || adOn);
  /*
   * Die Verbindung zur Kamera haengt, waehrend der Sender selbst verbunden
   * ist -- Funkloch, Netzwechsel. Das letzte Bild bleibt stehen, bis die
   * Verbindung neu aufgebaut ist; der Hinweis sagt, dass daran gearbeitet wird.
   */
  const activePeer = active ? states[active.id] : undefined;
  const shaky = Boolean(mainStream) && (activePeer === 'disconnected' || activePeer === 'failed');
  // Solange der Hinweis steht, ist das Bild nicht live -- er nimmt den Platz
  // des Live-Abzeichens ein, statt darauf zu liegen.
  const rejoining = Boolean(active && (active.reconnecting || shaky));

  /*
   * Was die Regie am Ausgang geschaltet hat, gilt auch hier -- und nicht nur
   * im RTMP-Stream. Sonst hoerte ein Zuschauer etwas, was bei YouTube gerade
   * bewusst stumm ist, und sah ein Bild, das dort abgeblendet laeuft.
   */
  const audible = feedAudible(active?.feed, state.output, active?.kind) && !adOn;
  const blanked = Boolean(active) && !feedVisible(active?.feed, state.output);
  /*
   * Das kleine Fenster ist mit zu hoeren -- dieselbe Regel wie im Stream.
   * Der Ton laeuft ueber ein eigenes <audio>, das immer im Baum steht, und
   * nicht ueber das <video> des Fensters: das entsteht erst mit dem Fenster,
   * und ein frisch eingehaengtes Element mit Ton darf iOS ohne neuen Tipp
   * nicht abspielen -- das Fenster bliebe dann stehen. Das <audio> dagegen
   * war schon beim Tipp auf „Ton an" da und ist damit freigeschaltet.
   */
  const pipHeard = pipAudible(pipSource, active, state.output) && !adOn;
  // Das Master-Stumm der Regie gilt auch fuer den Spot -- wie im Ausgang.
  const spotHeard = adOn && !state.output.muted;
  /*
   * „Die Regie hat stumm geschaltet" gilt nur, wenn ueberhaupt eine Kamera
   * geschaltet ist. Ohne Hauptbild ist es schlicht still, und der Knopf soll
   * dann nicht behaupten, jemand habe etwas abgeschaltet.
   */
  const silenced = Boolean(active || pipSource || adOn) && !audible && !pipHeard && !spotHeard;
  // Haengt immer am Fenster, geschaltet wird nur `muted` -- so laeuft das
  // Element durch, und ein spaeteres Lautschalten braucht keinen neuen Start.
  const pipAudioRef = useVideoStream<HTMLAudioElement>(overlayStream);

  useEffect(() => {
    // Zwei Gruende fuer Stille: dieser Zuschauer selbst oder die Regie.
    if (mainRef.current) mainRef.current.muted = muted || !audible;
    if (pipAudioRef.current) pipAudioRef.current.muted = muted || !pipHeard;
  }, [muted, audible, pipHeard, mainRef, mainStream, pipAudioRef, overlayStream]);

  const toggleSound = () => {
    const next = !muted;
    setMuted(next);
    if (next) return;
    // Noch im Tipp abspielen: Safari gibt Ton nur frei, was waehrend einer
    // Beruehrung gestartet wird -- ein Effekt danach kaeme zu spaet.
    for (const [element, heard] of [
      [mainRef.current, audible],
      [pipAudioRef.current, pipHeard],
    ] as const) {
      if (!element) continue;
      element.muted = !heard;
      void element.play().catch(() => undefined);
    }
    /*
     * Der Spot auch dann, wenn gerade keine Pause laeuft: ein Element, das
     * einmal im Tipp gestartet wurde, darf iOS spaeter mit Ton abspielen.
     * Ohne Pause wird es sofort wieder angehalten -- es hat ja nichts geladen.
     */
    const spot = spotRef.current;
    if (spot) {
      spot.muted = adOn && !spotHeard;
      void spot.play().catch(() => undefined);
      if (!adOn) spot.pause();
    }
  };

  const courseFull = courseShown && !adOn;
  const sourceShown = Boolean(mainStream) && !blanked && !state.output.blanked ? active : null;
  // Im Splitscreen hat jede Haelfte ihr eigenes Namensschild -- das Quellenschild
  // unten links stuende sonst womoeglich unter der falschen Kamera.
  const sourceInPlayer = !compact && !split && !layers.hidden.has('source') ? sourceShown : null;

  /*
   * Geschaltet werden kann nur, was gerade wirklich im Bild steht -- ein
   * Schalter fuer eine leere Notiz waere einer, der nichts tut. Das
   * Quellenschild steht hochkant unter dem Bild und verdeckt dort nichts,
   * also gibt es dann auch keinen Schalter dafuer.
   */
  const layerItems: LayerItem[] = [
    ...WIDGETS.filter(
      (w) =>
        state.widgets[w.id]?.on &&
        widgetHasContent(w.id, state.ticker, state.tickerFeed, sport, state.weather, course),
    ).map((w) => ({ id: w.id, label: w.label, zoomable: true })),
    // „mit Ton", weil Ausblenden hier auch den Ton nimmt: wer das Fenster
    // nicht sieht, holt es gar nicht erst.
    ...(overlay && !split
      ? [
          {
            id: 'pip' as const,
            label: 'Kleine Kamera',
            hint: pipAudible(overlay, active, state.output) ? `${overlay.label} · mit Ton` : overlay.label,
          },
        ]
      : []),
    ...(sourceShown && !compact && !split
      ? [{ id: 'source' as const, label: 'Kamera-Info', hint: sourceShown.label }]
      : []),
  ];
  const hiddenCount = layerItems.filter((item) => layers.hidden.has(item.id)).length;
  const menuOpen = layerMenu && layerItems.length > 0;

  const controls = (
    <>
      {layerItems.length > 0 && (
        <button
          ref={layerButtonRef}
          type="button"
          className={`player__ctrl${menuOpen ? ' is-on' : ''}`}
          onClick={() => setLayerMenu((v) => !v)}
          aria-expanded={menuOpen}
          aria-label={hiddenCount ? `Einblendungen, ${hiddenCount} ausgeblendet` : 'Einblendungen'}
          title="Einblendungen ein- oder ausblenden"
        >
          <LayersIcon />
          <span className="player__ctrl-label">Einblendungen</span>
          {hiddenCount > 0 && (
            <span className="player__ctrl-count" aria-hidden>
              {hiddenCount}
            </span>
          )}
        </button>
      )}
      {/* Am Video, nicht nur unter dem Ticker: am Handy liegt der hinter einem Reiter.
          Im Vollbild nicht -- dort ist nur der Player zu sehen, das Fenster nicht. */}
      {state.self && state.self.role !== 'organizer' && !fullscreen.active && state.myClips.policy !== 'off' && (
        <button
          type="button"
          className="player__ctrl player__ctrl--clip"
          onClick={() => setClipOpen(true)}
          title="Video- oder Sprachnachricht an die Regie"
          aria-label="Botschaft an die Regie"
        >
          🎬<span className="player__ctrl-label">Botschaft</span>
        </button>
      )}
      <button
        type="button"
        className="player__ctrl"
        onClick={toggleSound}
        /* Am Ausgang stumm: der Knopf bleibt bedienbar, sagt aber,
           dass hier gerade nichts zu holen ist. */
        title={silenced ? 'Die Regie hat den Ton aus' : muted ? 'Ton einschalten' : 'Stummschalten'}
      >
        {silenced ? '🔈' : muted ? '🔇' : '🔊'}
        <span className="player__ctrl-label">{silenced ? 'Regie stumm' : muted ? 'Ton an' : 'Ton aus'}</span>
      </button>
      {fullscreen.active && (
        <button
          type="button"
          className={`player__ctrl${chatOverlay ? ' is-on' : ''}`}
          onClick={() => setChatOverlay((v) => !v)}
          title="Chat ein- oder ausblenden"
        >
          💬<span className="player__ctrl-label">Chat</span>
        </button>
      )}
      <button
        type="button"
        className="player__ctrl"
        onClick={fullscreen.toggle}
        title={fullscreen.active ? 'Vollbild verlassen' : 'Vollbild'}
        aria-label={fullscreen.active ? 'Vollbild verlassen' : 'Vollbild'}
      >
        {fullscreen.active ? '⤡' : '⤢'}
        <span className="player__ctrl-label">{fullscreen.active ? 'Verlassen' : 'Vollbild'}</span>
      </button>
    </>
  );

  const menu = menuOpen && (
    <LayerMenu
      className={compact ? 'layer-menu--sheet' : 'layer-menu--pop'}
      items={layerItems}
      hidden={layers.hidden}
      onToggle={layers.toggle}
      onAll={(show) => layers.setMany(layerItems.map((item) => item.id), !show)}
      onClose={closeLayerMenu}
      anchorRef={layerButtonRef}
      zoom={layerZoom.zoom}
      onZoom={layerZoom.step}
      onZoomReset={layerZoom.reset}
    />
  );

  /*
   * Ungelesenes im Chat-Reiter, solange der andere offen ist -- und neben dem
   * Video, solange der Chat eingeklappt ist. Gemerkt wird die letzte gesehene
   * Nachricht, nicht die Anzahl -- der Verlauf ist gedeckelt.
   */
  const lastChatId = state.chat.length ? state.chat[state.chat.length - 1].id : null;
  const seenChatId = useRef<string | null>(lastChatId);
  if (stacked ? pane === 'chat' : chatOpen) seenChatId.current = lastChatId;
  const seenAt = seenChatId.current ? state.chat.findIndex((m) => m.id === seenChatId.current) : -1;
  const unreadChat = lastChatId === seenChatId.current ? 0 : seenAt >= 0 ? state.chat.length - 1 - seenAt : state.chat.length;
  const raceTab = showsRaceList(state.ticker.mode)
    ? 'Rennen'
    : state.ticker.mode === 'match' || state.ticker.mode === 'tennis'
      ? 'Spiel'
      : 'Ticker';
  /*
   * Mit Teilnehmerliste werden aus „Rennen" zwei Reiter: Teilnehmer und
   * Ereignisse. Ein Umschalter im Reiter waere eine zweite Reihe Reiter
   * direkt unter der ersten -- so ist es ein Tipp statt zwei.
   */
  const splitRace = tickerHasList(state.ticker);
  const paneTabs: { id: string; label: string; active: boolean; open: () => void }[] = [
    { id: 'chat', label: 'Chat', active: pane === 'chat', open: () => setPane('chat') },
    ...(splitRace
      ? [
          {
            id: 'list',
            label: 'Teilnehmer',
            active: pane === 'race' && tickerView === 'list',
            open: () => {
              setPane('race');
              setTickerView('list');
            },
          },
          {
            id: 'feed',
            label: 'Ereignisse',
            active: pane === 'race' && tickerView === 'feed',
            open: () => {
              setPane('race');
              setTickerView('feed');
            },
          },
        ]
      : [{ id: 'race', label: raceTab, active: pane === 'race', open: () => setPane('race') }]),
  ];

  return (
    <div className="stage-page">
      <div className="stage-bar">
        <Link to={`/events/${slug}`} className="btn btn--sm btn--onstage">
          ← Event
        </Link>
        <div>
          <div className="stage-bar__title">{state.event?.title ?? 'Hauptstream'}</div>
          <div className="stage-bar__sub">
            {state.event?.club} · {state.presence.total} online · {approved.filter((s) => s.live).length} Kameras
          </div>
        </div>
        <div className="stage-bar__spacer" />
        <ConnPill status={state.status} />
        <AppearanceControls />
        <Link to={`/events/${slug}/senden`} className="btn btn--sm">
          <span className="only-wide">Selbst </span>senden
        </Link>
      </div>

      <div className={`watch-layout${stacked ? ` is-stacked show-${pane}` : ''}${chatFolded ? ' is-chat-folded' : ''}`}>
        <div className="watch-main">
          <div
            className={`player-stage${fullscreen.active ? ' is-fullscreen' : ''}${
              fullscreen.active && chatOverlay ? ' has-chat' : ''
            }`}
            ref={stageRef}
          >
          <div
            className="player"
            {...playerChrome(
              {
                badge: !compact && Boolean(mainStream),
                controls: !compact,
                source: Boolean(sourceInPlayer),
                pip: Boolean(pipSource),
                compact,
                course: courseFull,
              },
              widgets,
              state.pip,
            )}
            {...backdropChrome(state.stage, state.event)}
            {...adChrome(ad, state.ads, Boolean(mainStream) && !blanked && !state.output.blanked)}
            {...adFrameChrome(frame)}
            {...courseChrome(courseFull, widgets.course, {
              live: Boolean(mainStream) && !blanked && !state.output.blanked,
              pipSlot: pipSource && !split && !state.output.blanked ? state.pip.slot : null,
              logoSlot: sponsorBug(state.ads)?.config.slot ?? null,
            })}
          >
            <PlayerBackdrop stage={state.stage} event={state.event} />
            {/* Alles, was zum Programm gehoert -- rueckt fuer Banner und L-Form als Ganzes zusammen. */}
            <div className="player__program">
              {fill && <SplitFill className="split-fill" stream={mainStream} blank={blanked} />}
              <video
                ref={mainRef}
                className={blanked ? 'is-blank' : undefined}
                autoPlay
                playsInline
                muted={muted || !audible}
                style={videoLook(lookOf(state.cameras, active?.id))}
              />
              {/* Ton des kleinen Fensters -- steht immer im Baum, siehe pipAudioRef. */}
              <audio ref={pipAudioRef} autoPlay muted />
              {(blanked || state.output.blanked) && <StandbyCard event={state.event} stage={state.stage} />}
              {/* Steht immer im Baum -- siehe AdSpot. */}
              <AdSpot ad={ad} muted={muted || !spotHeard} videoRef={spotRef} onSoundBlocked={() => setMuted(true)} />
              <AdLabel ad={ad} live={Boolean(mainStream) && !blanked && !state.output.blanked} />

              {/* Bedienung liegt ausserhalb der Bild-Bedingung -- Vollbild soll
                  auch gehen, wenn die Regie gerade schwarz geschaltet hat. */}
              {!compact && <div className="player__controls">{controls}</div>}

              {/*
                * Kurz weg heiszt nicht abgebrochen: waehrend der Sender neu
                * verbindet (Reload, Tabwechsel, Funkloch) bleibt das letzte Bild
                * stehen. Ohne diesen Hinweis sieht das aus wie ein Standbild
                * ohne Erklaerung.
                */}
              {active && rejoining && (
                <div className="player__reconnect">
                  <span className="dot dot--pulse" />
                  {active.reconnecting ? `${active.displayName} verbindet neu …` : 'Verbindung wird neu aufgebaut …'}
                </div>
              )}

              {mainStream && !compact && !rejoining && (
                <div className="player__badge">
                  <span className="dot dot--pulse" />
                  Live
                </div>
              )}
              {sourceInPlayer && (
                <div className="player__source">
                  <b>{sourceInPlayer.label}</b>
                  <span>{byline(sourceInPlayer)}</span>
                </div>
              )}

              {/* Bild-im-Bild: zweite Quelle, die die Regie dazugeschaltet hat */}
              {pipSource && !state.output.blanked && (
                <div className="pip">
                  {fill && <SplitFill className="pip__fill" stream={overlayStream} blank={!camOn(pipSource.feed)} />}
                  <video
                    ref={pipRef}
                    className={camOn(pipSource.feed) ? undefined : 'is-blank'}
                    autoPlay
                    playsInline
                    muted
                    style={videoLook(lookOf(state.cameras, pipSource.id))}
                  />
                  {!overlayStream && <div className="pip__idle">{peerStateLabel[states[pipSource.id] ?? 'new']}</div>}
                  <span className="pip__label">
                    {pipSource.label}
                    {pipSource.bib ? ` · Nr. ${pipSource.bib}` : ''}
                  </span>
                </div>
              )}
              {split && sourceShown && (
                <span className="split-label">
                  {sourceShown.label}
                  {sourceShown.bib ? ` · Nr. ${sourceShown.bib}` : ''}
                </span>
              )}

              {/* Einblendungen der Regie -- liegen als HTML ueber dem Bild und
                  gehen deshalb auch ins Vollbild mit. */}
              <StreamOverlay
                ticker={state.ticker}
                feed={state.tickerFeed}
                widgets={widgets}
                sport={sport}
                maxRows={compact || flat ? COMPACT_LIST_ROWS : undefined}
                sponsor={sponsorBug(state.ads)}
                weather={state.weather}
                course={course}
                results={state.results}
                spotlight={state.spotlight}
                zoom={layerZoom.zoom}
              />

              {!mainStream && !blanked && !state.output.blanked && (
                <div className="player__idle">
                  <b>
                    {active
                      ? active.reconnecting
                        ? 'Die Kamera verbindet neu …'
                        : 'Verbinde mit der Kamera …'
                      : 'Noch kein Bild im Hauptstream'}
                  </b>
                  <span>
                    {active
                      ? peerStateLabel[states[active.id] ?? 'new']
                      : 'Der Veranstalter hat noch keine Kamera aufs Hauptbild gelegt.'}
                  </span>
                </div>
              )}

              {!compact && menu}
            </div>
            <AdFrameLayer view={frame} ads={state.ads} />
          </div>

            {/* Hochkant: was sonst im Bild liegt, steht hier darunter. */}
            {compact && (
              <div className="player-bar">
                <div className="player-bar__info">
                  {adOn ? (
                    <span className="player-bar__live player-bar__live--ad">Werbung</span>
                  ) : (
                    mainStream && (
                      <span className="player-bar__live">
                        <span className="dot dot--pulse" />
                        Live
                      </span>
                    )
                  )}
                  {sourceShown && (
                    <span className="player-bar__source">
                      <b>{sourceShown.label}</b>
                      <span>{byline(sourceShown)}</span>
                    </span>
                  )}
                </div>
                <div className="player-bar__controls">{controls}</div>
              </div>
            )}
            {compact && menu}

            {/* Im Vollbild liegt der Chat ueber dem Bild -- er muss im
                Vollbild-Element haengen, sonst waere er nicht sichtbar. */}
            {fullscreen.active && chatOverlay && (
              <div className="chat-overlay">
                <ChatPanel
                  messages={state.chat}
                  presence={state.presence}
                  disabled={state.status !== 'open'}
                  onSend={(text) => send({ t: 'chat:send', text })}
                />
              </div>
            )}
          </div>

          {stacked && (
            <div className="watch-tabs" role="tablist" aria-label="Unter dem Video">
              {paneTabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={tab.active}
                  className={tab.active ? 'is-active' : undefined}
                  onClick={tab.open}
                >
                  {tab.label}
                  {tab.id === 'chat' && unreadChat > 0 && (
                    <span className="watch-tabs__badge" aria-label={`${unreadChat} neu`}>
                      {unreadChat > 99 ? '99+' : unreadChat}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          <div className="watch-race">
            <LiveTicker
              ticker={state.ticker}
              feed={state.tickerFeed}
              sport={sport}
              tone="stage"
              limit={20}
              live={state.event?.status === 'live'}
              view={tickerView}
              onView={setTickerView}
              tabs={!stacked}
            />

            {state.self?.role !== 'organizer' && (
              <ClipRecorder
                slug={slug}
                connectionId={state.self?.connectionId ?? null}
                mine={state.myClips}
                send={send}
                onActive={onClipRecorder}
                open={clipOpen}
                onOpenChange={setClipOpen}
              />
            )}

            <div>
              <div className="row gap-8" style={{ marginBottom: 10 }}>
                <h3 style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '.1em', color: 'var(--stage-muted)' }}>
                  Kameras im Event
                </h3>
                <span className="stage-bar__sub" style={{ marginLeft: 'auto' }}>
                  Umschalten macht die Regie
                </span>
              </div>
              <div className="sources-rail">
                {approved.map((source) => (
                  <SourceChip
                    key={source.id}
                    source={source}
                    isActive={source.id === activeSourceId}
                    isOverlay={source.id === overlaySourceId}
                  />
                ))}
                {approved.length === 0 && (
                  <p className="stage-bar__sub">Noch keine freigegebene Kamera. Anfragen laufen bei der Regie auf.</p>
                )}
              </div>
            </div>
            <StageLegal />
          </div>
        </div>

        {/* Eingeklappt bleibt der Chat geladen und nur verborgen -- ein
            angefangener Satz und die Leseposition ueberstehen das Klappen. */}
        {!fullscreen.active && chatFolded && (
          <button
            ref={chatRailRef}
            type="button"
            className="director-rail"
            onClick={() => foldChat(true)}
            aria-label={`Chat einblenden${unreadChat ? ` (${unreadChat} neu)` : ''}`}
            title="Chat einblenden"
          >
            <span className="director-rail__icon">
              <Chevron dir="left" />
            </span>
            {unreadChat > 0 && <span className="director-rail__count">{unreadChat > 99 ? '99+' : unreadChat}</span>}
            <span className="director-rail__label">Chat</span>
          </button>
        )}
        {!fullscreen.active && (
          <ChatPanel
            messages={state.chat}
            presence={state.presence}
            disabled={state.status !== 'open'}
            onSend={(text) => send({ t: 'chat:send', text })}
            headAction={
              stacked ? undefined : (
                <button
                  ref={chatFoldRef}
                  type="button"
                  className="director-fold"
                  onClick={() => foldChat(false)}
                  aria-label="Chat einklappen"
                  title="Einklappen"
                >
                  <Chevron dir="right" />
                </button>
              )
            }
          />
        )}
      </div>
    </div>
  );
}

function SourceChip({
  source,
  isActive,
  isOverlay,
}: {
  source: StreamSource;
  isActive: boolean;
  isOverlay: boolean;
}) {
  return (
    <div className={`source-chip${isActive || isOverlay ? ' is-active' : ''}`}>
      <div className="source-chip__thumb">
        <div className="placeholder">
          {isActive
            ? 'im Hauptstream'
            : isOverlay
              ? 'im kleinen Fenster'
              : source.live
                ? 'sendet'
                : 'freigegeben, noch kein Bild'}
        </div>
      </div>
      <div className="source-chip__info">
        <b>
          {source.kind === 'screen' ? '🖥 ' : ''}
          {source.label}
        </b>
        <span>{byline(source)}</span>
      </div>
    </div>
  );
}

/**
 * Wer hinter einer Quelle steht. Bei einem RTMP-Eingang (Drohne) ist der Name
 * schon die Bezeichnung -- „Drohne · Drohne" saehe nach einem Fehler aus.
 */
function byline(source: StreamSource): string {
  if (source.ingest) return roleLabel[source.role];
  return `${source.displayName} · ${roleLabel[source.role]}${source.bib ? ` · Nr. ${source.bib}` : ''}`;
}
